import React, { useEffect, useState, useCallback } from "react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import * as XLSX from "xlsx";
import "./ManagementDashboard.css";

const VILLA_COLLECTION = "ClarksExoticaVilla";
const BOOKINGS_COLLECTION = "villaBookings";
const INVOICES_COLLECTION = "villaInvoices";
const LEADS_COLLECTION = "leads"; // keep if you still use it

const safeNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v.toString().replace(/,/g, "")) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const paiseToRupees = (paise) => {
  const p = safeNum(paise);
  return p > 0 ? p / 100 : 0;
};

const toDateLabel = (ts) => {
  try {
    if (!ts) return "N/A";
    const d =
      typeof ts === "string"
        ? new Date(ts)
        : ts?.toDate
        ? ts.toDate()
        : new Date(ts);
    if (!d || isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-IN");
  } catch {
    return "N/A";
  }
};

const Report = ({ setError = () => {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  const [villas, setVillas] = useState([]);
  const [villaBookings, setVillaBookings] = useState([]);
  const [villaInvoices, setVillaInvoices] = useState([]);
  const [leads, setLeads] = useState([]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setReportError(null);

    try {
      // last 30 days filter
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // ✅ 1) Villas Inventory
      const villaRef = collection(db, VILLA_COLLECTION);
      const villaQ = query(villaRef, orderBy("villaId", "asc"));
      const villaSnap = await getDocs(villaQ);
      const villaData = villaSnap.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));
      setVillas(villaData);

      // ✅ 2) Bookings (clients)
      const bookingRef = collection(db, BOOKINGS_COLLECTION);

      // If you have createdAt in villaBookings as timestamp, keep this orderBy.
      // Otherwise remove orderBy and do sorting in JS.
      const bookingQ = query(bookingRef, orderBy("createdAt", "desc"));
      const bookingSnap = await getDocs(bookingQ);
      const bookingData = bookingSnap.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));
      setVillaBookings(bookingData);

      // ✅ 3) Invoices
      const invoiceRef = collection(db, INVOICES_COLLECTION);
      const invoiceQ = query(invoiceRef, orderBy("timestamp", "desc")); // timestamp is ISO string
      const invoiceSnap = await getDocs(invoiceQ);
      const invoiceData = invoiceSnap.docs.map((d) => ({ firestoreId: d.id, ...d.data() }));
      setVillaInvoices(invoiceData);

      // ✅ 4) Leads (optional)
      // If you don’t use leads now, you can comment this block + UI button
      const leadsRef = collection(db, LEADS_COLLECTION);
      const leadsQ = query(
        leadsRef,
        where("timestamp", ">=", thirtyDaysAgo),
        orderBy("timestamp", "desc")
      );
      const leadsSnap = await getDocs(leadsQ);
      const leadsData = leadsSnap.docs.map((d) => ({ leadId: d.id, ...d.data() }));
      setLeads(leadsData);

      if (!villaData.length && !bookingData.length && !invoiceData.length && !leadsData.length) {
        setReportError("No data available for reports.");
      }
    } catch (err) {
      console.error("Report fetch error:", err);
      setError("Failed to fetch report data.");
      setReportError(err?.message || "Failed to fetch report data.");
    } finally {
      setIsLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ------------------ EXCEL DOWNLOADS ------------------

  const downloadVillasExcel = () => {
    if (villas.length === 0) {
      setReportError("No villa inventory data available to download.");
      return;
    }

    const rows = villas.map((v) => ({
      "Villa Firestore Doc": v.firestoreId || "N/A",
      "Villa ID": v.villaId || "N/A",
      "Villa Number": v.villaNumber || "N/A",
      Configuration: v.configuration || "N/A",
      Facing: v.facing || "N/A",
      "Built-up Sqft": v.builtUpAreaSqft ?? "N/A",
      "Land Sqft": v.LandAreaSqft ?? v.landAreaSqft ?? "N/A",
      "Premium Charges": v.premiumCharges ?? "N/A",
      "Inclusive Price": v.inclusivePrice ?? "N/A",
      Status: v.Status || "N/A",
      "Floor Plan": v.floorPlan || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Villa Inventory");
    XLSX.writeFile(wb, `villa_inventory_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const downloadVillaBookingsExcel = () => {
    if (villaBookings.length === 0) {
      setReportError("No villaBookings data available to download.");
      return;
    }

    const rows = villaBookings.map((b) => ({
      "Booking Firestore Doc": b.firestoreId || "N/A",
      "Booking Number": b.bookingNumber || "N/A",
      "Created At": toDateLabel(b.createdAt),
      "Updated At": toDateLabel(b.updatedAt),

      Name: b?.applicant1?.name || b.name || "N/A",
      Email: b?.applicant1?.email || b.email || "N/A",
      Mobile: b?.applicant1?.mobileNumber || b.mobileNumber || "N/A",
      PAN: b?.applicant1?.pan || b.pan || "N/A",

      "Applicant2 Name": b?.applicant2?.name || "N/A",
      "Applicant2 PAN": b?.applicant2?.pan || "N/A",

      "Villa ID": b.villaId || "N/A",
      "Villa Number": b.villaNumber || "N/A",
      "Villa FirestoreId": b.villaFirestoreId || "N/A",

      Configuration: b.configuration || "N/A",
      Facing: b.facing || "N/A",
      "Built-up Sqft": b.builtUpAreaSqft ?? "N/A",
      "Land Sqft": b.landAreaSqft ?? b.LandAreaSqft ?? "N/A",

      "Payment Status": b.paymentStatus || "N/A",
      "Advance (₹)": b.advanceAmountPaise ? paiseToRupees(b.advanceAmountPaise) : "N/A",

      Source: b.source || "N/A",
      "Source Details": b.sourceDetails || "N/A",

      "KYC Documents":
        b.kycDocuments && Array.isArray(b.kycDocuments)
          ? b.kycDocuments.map((d) => `${d.type}: ${d.url}`).join(" | ")
          : "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Villa Bookings");
    XLSX.writeFile(wb, `villa_bookings_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const downloadVillaInvoicesExcel = () => {
    if (villaInvoices.length === 0) {
      setReportError("No villaInvoices data available to download.");
      return;
    }

    const rows = villaInvoices.map((inv) => ({
      "Invoice Firestore Doc": inv.firestoreId || "N/A",
      bookingId: inv.bookingId || "N/A",
      bookingNumber: inv.bookingNumber || "N/A",
      timestamp: inv.timestamp || "N/A",
      "Date": toDateLabel(inv.timestamp),
      status: inv.status || "N/A",
      BookingStatus: inv.BookingStatus || "N/A",
      "Advance (₹)": inv.advanceAmountPaise ? paiseToRupees(inv.advanceAmountPaise) : "N/A",
      villaId: inv.villaId || "N/A",
      villaNumber: inv.villaNumber || "N/A",
      villaFirestoreId: inv.villaFirestoreId || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Villa Invoices");
    XLSX.writeFile(wb, `villa_invoices_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const downloadLeadsExcel = () => {
    if (leads.length === 0) {
      setReportError("No lead data available to download.");
      return;
    }

    const rows = leads.map((lead) => ({
      "Lead ID": lead.leadId || "N/A",
      "Date of Entry": toDateLabel(lead.timestamp),
      Name: lead.name || "N/A",
      "Mobile Number": lead.mobileNumber || "N/A",
      "Email ID": lead.email || "N/A",
      "Interested Project": lead.projectName || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads (Last 30 Days)");
    XLSX.writeFile(wb, `leads_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="md-section-content">
      <div className="md-section-header">
        <h2 className="md-section-title">Reports</h2>

        <button className="md-close-tab-button" onClick={fetchAll} disabled={isLoading}>
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {isLoading ? (
        <div className="md-loading">Loading...</div>
      ) : reportError ? (
        <div className="md-error">{reportError}</div>
      ) : (
        <div className="md-report-container">
          <div className="md-report-buttons">
            <button className="md-report-button" onClick={downloadVillasExcel}>
              ⬇️ Villa Inventory
            </button>
            <button className="md-report-button" onClick={downloadVillaBookingsExcel}>
              ⬇️ Villa Bookings
            </button>
            <button className="md-report-button" onClick={downloadVillaInvoicesExcel}>
              ⬇️ Villa Invoices
            </button>
            <button className="md-report-button" onClick={downloadLeadsExcel}>
              ⬇️ Leads (30 Days)
            </button>
          </div>

          {/* Quick List (Leads) */}
          <div className="md-entry-form-reports">
            <h3>Entry Form Reports (Leads - 30 days)</h3>

            <div className="md-entry-form-fields md-sticky-header">
              <div>Date of Entry</div>
              <div>Name</div>
              <div>Mobile Number</div>
              <div>Email</div>
              <div>Interested Project</div>
            </div>

            {leads.length === 0 ? (
              <p className="md-no-data">No leads available.</p>
            ) : (
              <div className="md-leads-container-scrollable">
                <div className="md-leads-container">
                  {leads.map((lead) => (
                    <div key={lead.leadId} className="md-lead-item">
                      <div>{toDateLabel(lead.timestamp)}</div>
                      <div>{lead.name || "N/A"}</div>
                      <div>{lead.mobileNumber || "N/A"}</div>
                      <div>{lead.email || "N/A"}</div>
                      <div>{lead.projectName || "N/A"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Small stats row */}
          <div className="md-report-stats">
            <div className="md-report-stat-card">
              <div className="md-report-stat-title">Villas</div>
              <div className="md-report-stat-value">{villas.length}</div>
            </div>
            <div className="md-report-stat-card">
              <div className="md-report-stat-title">Bookings</div>
              <div className="md-report-stat-value">{villaBookings.length}</div>
            </div>
            <div className="md-report-stat-card">
              <div className="md-report-stat-title">Invoices</div>
              <div className="md-report-stat-value">{villaInvoices.length}</div>
            </div>
            <div className="md-report-stat-card">
              <div className="md-report-stat-title">Leads (30 days)</div>
              <div className="md-report-stat-value">{leads.length}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Report;
